import { useState, useEffect, useMemo, type ReactNode } from 'react';
import {
  Badge,
  Button,
  Field,
  FieldError,
  FieldLabel,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
} from '@monksflow/monks-ui';
import { PencilIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import {
  useEtlContentSources,
  useAddContentSource,
  useUpdateContentSource,
  useDeleteContentSource,
} from '@/features/admin/model';
import { Loading, DeleteConfirmationModal, SearchInput } from '@/shared/ui';
import { TagInputField } from '@/shared/ui/form';
import { FormSidePanel } from '@/shared/ui/layout/FormSidePanel';
import { useDebounce } from '@/shared/hooks';
import {
  EtlContentSourceDto,
  CreateEtlContentSourceDto,
  UpdateEtlContentSourceDto,
} from '@inference.engine/api-client/web';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { SEARCH_DEBOUNCE_MS } from '@/shared/constants/search.constants';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { AdminPageShell } from '../AdminPageShell';
const PLATFORM_LABEL_KEY = {
  reddit: 'admin.etlConfig.reddit',
  youtube: 'admin.etlConfig.youTube',
  x: 'admin.etlConfig.xTwitter',
} as const;

/* eslint-disable i18next/no-literal-string -- wire enum values for the source platform; labelKey carries the copy */
const PLATFORMS = [
  { value: 'reddit', labelKey: PLATFORM_LABEL_KEY.reddit },
  { value: 'youtube', labelKey: PLATFORM_LABEL_KEY.youtube },
  { value: 'x', labelKey: PLATFORM_LABEL_KEY.x },
] as const;
/* eslint-enable i18next/no-literal-string */

// A factory, not a module-scope const: building the schema at import time would bake
// in whatever language was active then, and the messages could never follow a change.
const buildContentSourceSchema = (t: TFunction<'validation'>) =>
  z.object({
    platform: z.enum(['reddit', 'youtube', 'x'], {
      error: t('admin.etlConfig.platformIsRequired'),
    }),
    topic: z.string().min(1, t('admin.etlConfig.topicIsRequired')).trim(),
    domains: z.array(z.string().min(1)).min(1, t('admin.etlConfig.atLeastOneDomainIsRequired')),
  });

type ContentSourceFormData = z.infer<ReturnType<typeof buildContentSourceSchema>>;

type ContentSourcePlatform = (typeof PLATFORMS)[number]['value'];

/**
 * The platform picker is the design system's own `Select`. Its menu stays inside
 * the sheet's dismissable layer, which a portalled menu from another library
 * does not.
 */
function PlatformSelect({
  value,
  onChange,
  onBlur,
}: {
  value: ContentSourcePlatform;
  onChange: (value: ContentSourcePlatform) => void;
  onBlur: () => void;
}) {
  const { t } = useTranslation('admin');

  return (
    <Select value={value} onValueChange={(next) => onChange(next as ContentSourcePlatform)}>
      <SelectTrigger id="etl-platform" className="w-full" onBlur={onBlur}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {PLATFORMS.map((platform) => (
          <SelectItem key={platform.value} value={platform.value}>
            {t(platform.labelKey)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** The page's title and its legacy white card, bound once for both states. */
function EtlConfigShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation('admin');

  return <AdminPageShell title={t('admin.etlConfig.etlConfig')}>{children}</AdminPageShell>;
}

export const EtlConfigPage = () => {
  const { t } = useTranslation('admin');
  const { t: tValidation } = useTranslation('validation');

  const { data: contentSources, isLoading } = useEtlContentSources();
  const addMutation = useAddContentSource();
  const updateMutation = useUpdateContentSource();
  const deleteMutation = useDeleteContentSource();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingSource, setEditingSource] = useState<EtlContentSourceDto | null>(null);
  const [deletingSource, setDeletingSource] = useState<EtlContentSourceDto | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, SEARCH_DEBOUNCE_MS);
  const isFiltering = searchQuery !== debouncedSearchQuery;

  const formSchema = useMemo(() => buildContentSourceSchema(tValidation), [tValidation]);

  const form = useForm<ContentSourceFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      platform: 'reddit',
      topic: '',
      domains: [],
    },
    mode: 'onTouched',
  });

  // Reset form when modal opens/closes or editing source changes
  useEffect(() => {
    if (isAddModalOpen && !editingSource) {
      form.reset({
        platform: 'reddit',
        topic: '',
        domains: [],
      });
    }
  }, [isAddModalOpen, editingSource, form]);

  useEffect(() => {
    if (editingSource) {
      form.reset({
        platform: editingSource.platform as 'reddit' | 'youtube' | 'x',
        topic: editingSource.topic,
        domains: editingSource.domains,
      });
    }
  }, [editingSource, form]);

  const handleOpenAddModal = () => {
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    form.reset();
  };

  const handleOpenEditModal = (source: EtlContentSourceDto) => {
    setEditingSource(source);
  };

  const handleCloseEditModal = () => {
    setEditingSource(null);
    form.reset();
  };

  const handleCloseForm = () => {
    if (editingSource) {
      handleCloseEditModal();
    } else {
      handleCloseAddModal();
    }
  };

  const handleSubmit = form.handleSubmit((data) => {
    if (editingSource) {
      const updateData: UpdateEtlContentSourceDto = {
        id: editingSource.id,
        platform: data.platform,
        topic: data.topic,
        domains: data.domains,
      };
      updateMutation.mutate(updateData, {
        onSuccess: () => {
          handleCloseEditModal();
        },
      });
    } else {
      const createData: CreateEtlContentSourceDto = {
        platform: data.platform,
        topic: data.topic,
        domains: data.domains,
      };
      addMutation.mutate(createData, {
        onSuccess: () => {
          handleCloseAddModal();
        },
      });
    }
  });

  const handleDelete = () => {
    if (deletingSource) {
      deleteMutation.mutate(deletingSource.id, {
        onSuccess: () => {
          setDeletingSource(null);
        },
      });
    }
  };

  const isSubmitting = addMutation.isPending || updateMutation.isPending;

  // Filter content sources by topic using regex
  const filteredContentSources = useMemo(() => {
    if (!contentSources || contentSources.length === 0) {
      return [];
    }

    if (!debouncedSearchQuery.trim()) {
      return contentSources;
    }

    try {
      // eslint-disable-next-line i18next/no-literal-string -- regular expression flag
      const regex = new RegExp(debouncedSearchQuery, 'i');
      return contentSources.filter((source) => regex.test(source.topic));
    } catch {
      // If regex is invalid, fall back to simple string matching
      const searchLower = debouncedSearchQuery.toLowerCase();
      return contentSources.filter((source) => source.topic.toLowerCase().includes(searchLower));
    }
  }, [contentSources, debouncedSearchQuery]);

  // Group filtered content sources by platform
  const groupedByPlatform = useMemo(() => {
    if (filteredContentSources.length === 0) {
      return {};
    }

    const grouped: Record<string, EtlContentSourceDto[]> = {};
    filteredContentSources.forEach((source) => {
      const platform = source.platform;
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!grouped[platform]) {
        grouped[platform] = [];
      }
      grouped[platform].push(source);
    });

    return grouped;
  }, [filteredContentSources]);

  const platformOrder: Array<'reddit' | 'youtube' | 'x'> = ['reddit', 'youtube', 'x'];

  if (isLoading) {
    return (
      <EtlConfigShell>
        <Loading />
      </EtlConfigShell>
    );
  }

  return (
    <>
      <EtlConfigShell>
        <div className="flex items-center justify-between mb-6">
          {/* `ml-auto` rather than the row's own alignment: with the title gone
              the toolbar is the only child, and it belongs at the end either way. */}
          <div className="ml-auto flex flex-row items-center gap-4">
            <SearchInput
              placeholder={t('admin.etlConfig.searchByTopic')}
              aria-label={t('admin.etlConfig.searchContentSourcesLabel')}
              value={searchQuery}
              onChange={setSearchQuery}
              clearable
              className="w-72"
            />
            <Button type="button" onClick={handleOpenAddModal}>
              <PlusIcon />
              {t('admin.etlConfig.addContentSource')}
            </Button>
          </div>
        </div>

        {!contentSources || contentSources.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-base leading-6 text-muted-foreground">
              {t('admin.etlConfig.noContentSourcesFoundAdd')}
            </p>
          </div>
        ) : filteredContentSources.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-base leading-6 text-muted-foreground">
              {t('admin.etlConfig.noContentSourcesMatchYour')}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-8">
            {isFiltering && (
              <div className="flex justify-center items-center py-12">
                <Spinner className="size-12" />
              </div>
            )}
            {!isFiltering &&
              platformOrder.map((platform) => {
                const sources = groupedByPlatform[platform];
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                if (!sources || sources.length === 0) return null;

                return (
                  <div key={platform}>
                    <div className="flex items-center gap-2 mb-3">
                      <Badge>{t(PLATFORM_LABEL_KEY[platform])}</Badge>
                      <span className="text-sm leading-5 text-muted-foreground">
                        {t('admin.etlConfig.sourceCount', { count: sources.length })}
                      </span>
                    </div>

                    <div className="flex flex-wrap gap-4">
                      {sources.map((contentSource) => (
                        <div
                          key={contentSource.id}
                          className="border min-w-96 flex-1 border-border rounded-lg p-4 hover:shadow-md transition-shadow duration-base ease-out"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <Badge variant="outline">{contentSource.platform}</Badge>
                            <div className="flex flex-row gap-2">
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => handleOpenEditModal(contentSource)}
                              >
                                <PencilIcon />
                                {t('admin.etlConfig.edit')}
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="text-destructive"
                                onClick={() => setDeletingSource(contentSource)}
                              >
                                <Trash2Icon />
                                {t('admin.etlConfig.delete')}
                              </Button>
                            </div>
                          </div>
                          <p className="text-base font-semibold leading-6">{contentSource.topic}</p>
                          <div className="flex flex-wrap gap-1 mt-2 mb-4">
                            {contentSource.domains.map((domain, index) => (
                              <Badge key={index} variant="outline">
                                {domain}
                              </Badge>
                            ))}
                          </div>
                          <span className="mt-2 block text-xs leading-4 text-muted-foreground">
                            {t('admin.etlConfig.created', {
                              date: new Date(contentSource.created_at).toLocaleDateString(),
                            })}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </EtlConfigShell>
      <FormSidePanel
        open={isAddModalOpen || editingSource !== null}
        onOpenChange={(next) => !next && handleCloseForm()}
        title={
          editingSource
            ? t('admin.etlConfig.editContentSource')
            : t('admin.etlConfig.addContentSource')
        }
        className="w-[560px] gap-sm"
        footer={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={handleCloseForm}
              disabled={isSubmitting}
            >
              {t('admin.etlConfig.cancel')}
            </Button>
            <Button type="button" onClick={() => void handleSubmit()} disabled={isSubmitting}>
              {/* Hidden from the a11y tree so the button keeps its own name. */}
              {isSubmitting && <Spinner aria-hidden="true" />}
              {editingSource ? t('admin.etlConfig.update') : t('admin.etlConfig.add')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-lg">
          <Controller
            name="platform"
            control={form.control}
            render={({ field, fieldState: { error } }) => (
              <div className="flex flex-col gap-xs">
                <Label htmlFor="etl-platform">{t('admin.etlConfig.platform')}</Label>
                <PlatformSelect
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
                {error?.message && <p className="text-sm text-destructive">{error.message}</p>}
              </div>
            )}
          />

          <Field data-invalid={form.formState.errors.topic ? true : undefined}>
            <FieldLabel htmlFor="etl-topic">
              {t('admin.etlConfig.topic')}
              <span aria-hidden="true" className="text-destructive">
                *
              </span>
            </FieldLabel>
            <Input
              id="etl-topic"
              required
              placeholder={t('admin.etlConfig.eGSubredditNameChannel')}
              aria-invalid={!!form.formState.errors.topic}
              {...form.register('topic')}
            />
            {form.formState.errors.topic && (
              <FieldError>{form.formState.errors.topic.message}</FieldError>
            )}
          </Field>

          <Controller
            name="domains"
            control={form.control}
            render={({ field, fieldState: { error } }) => (
              <TagInputField
                id="etl-domains"
                value={field.value}
                onChange={field.onChange}
                label={t('admin.etlConfig.domains')}
                placeholder={t('admin.etlConfig.typeAndPressEnterTo')}
                freeSolo
                required
                options={[]}
                error={error?.message}
                helperText={t('admin.etlConfig.addDomainsByTypingAnd')}
              />
            )}
          />
        </div>
      </FormSidePanel>

      <DeleteConfirmationModal
        open={deletingSource !== null}
        onCancel={() => setDeletingSource(null)}
        onConfirmDelete={handleDelete}
        resourceType={t('admin.etlConfig.contentSourceResource')}
        resourceName={deletingSource?.topic}
        warningMessage={t('admin.etlConfig.thisActionCannotBeUndone')}
        isDeleting={deleteMutation.isPending}
      />
    </>
  );
};
